export const inspectionsArticles = [
    {
        id: 'inspections.overview',
        title: 'Site Inspections Overview',
        category: 'Inspections',
        order: 1,
        summary: 'Run quality and safety checklists on site — photos, notes, signatures and automatic defects',
        route: '/inspections',
        keywords: ['checklist', 'inspection', 'ITP', 'safety audit', 'quality', 'handover', 'PCI'],
        content: `
Inspections turn your quality and safety checklists into guided site walks. Pick a checklist template, walk the job answering each item (Pass / Fail / N/A), attach photos as you go, and sign off on your phone. Failed items raise **defects** automatically, and every submitted inspection produces a branded PDF report.

**Key Features:**

- **Checklist templates** — reusable definitions grouped into sections (stage ITPs, safety audits, handover QA)
- **Starter library** — one click adds 8 ready-made Australian residential templates (quality + safety)
- **Mobile-first conduct screen** — big Pass/Fail buttons, camera photos per item, notes
- **Automatic defects** — failing an item drafts a defect with the responsible trade, priority and due date
- **Signature & report** — sign on the screen; a PDF report with photos and results generates on demand
    `,
        expandableSections: [
            {
                title: 'Running an inspection',
                content: `
1. Go to **Site → Inspections** and click **New Inspection**
2. Pick the checklist template, the job, who it's assigned to and (optionally) a scheduled date
3. Click **Create & Start** to open the conduct screen (or leave it for the assignee — they get a notification)
4. Answer each item: **PASS / FAIL / N/A** (tap again to clear). Text, number, choice and photo items show the matching input
5. Use the **camera button** on any item to photograph it; the **note button** adds a comment
6. When an item **fails**, a defect draft opens under it — set the location, priority, assignee and trade
7. Click **Submit**, review the pass/fail summary, sign in the signature box and **Sign & Submit**

Required items (marked *) must be answered before submitting. The overall result is FAIL if any item failed.
        `,
            },
            {
                title: 'Checklist templates',
                content: `
Open **Inspections → Templates** to manage checklists:

- **Add starter templates** seeds the built-in library: Slab/Frame/Lock-up/Fixing Stage ITPs, PCI/Handover QA, Site Safety Audit, Daily Pre-Start and Scaffold checks. Seeding never overwrites — templates you already have are skipped
- **New Template** builds one from scratch: items have a section, question, response type, a *required* flag and a *photo on fail* prompt
- Editing a template never changes past inspections — each inspection snapshots its items when created
- Deleting a template that has been used archives it instead, keeping history intact
        `,
            },
            {
                title: 'Recurring schedules',
                content: `
Open **Inspections → the repeat icon** to manage recurring inspections (e.g. a weekly site safety walk):

1. Click **New Schedule**, pick the checklist, job, assignee, frequency (daily / weekly / fortnightly / monthly) and the first run date
2. Each morning the system creates any inspections that have fallen due, assigns them and notifies the assignee
3. Toggle a schedule off to pause it without deleting it

Assignees are also notified once when a scheduled inspection goes **overdue** (its date passed without being submitted), and defect assignees are notified when a defect passes its due date.
        `,
            },
            {
                title: 'Supervisor sign-off & sharing to the client portal',
                content: `
- Tick **Requires supervisor sign-off** on a template and its submitted inspections stay open until an owner/admin reviews them and clicks **Sign off & close** (their signature is added to the report).
- Use **Share to client portal** on a submitted inspection to make its report visible to the client on their portal (Inspections tab) — ideal for PCI/handover QA. Remove it again at any time.
- The KPI cards above the list track overdue inspections, items awaiting sign-off, the 30-day pass rate and open/overdue defects.
        `,
            },
            {
                title: 'Inspection reports',
                content: `
Submitted inspections generate a branded PDF: summary header (job, inspector, result counts), every item with its answer badge, notes, photo thumbnails and the inspector's signature. Open it from the inspection row menu (**Report PDF**) or from the conduct screen after submitting.
        `,
            },
        ],
    },
    {
        id: 'defects.overview',
        title: 'Defects Register',
        category: 'Inspections',
        order: 2,
        summary: 'Track punch-list defects from open to closed, with photos and trade assignment',
        route: '/defects',
        keywords: ['defect', 'punch list', 'snag', 'rectification', 'QA'],
        content: `
The Defects register tracks everything that needs fixing — raised automatically from failed inspection items or added manually. Each defect carries a job, location, priority, the staff member responsible, the trade/supplier who caused it, a due date and before/after photos.

**Status flow:** Open → In progress → Ready for review → Closed. Overdue defects show their due date in red.
    `,
        expandableSections: [
            {
                title: 'Working a defect',
                content: `
1. Open a defect from the register (filter by job, status or search)
2. **Start work** moves it to In progress; **Ready for review** hands it back for checking; **Close** completes it
3. Attach an **Issue photo** when raising it and a **Fixed photo** when done — both appear side by side
4. Assigning a defect notifies that person in-app and by email
5. Closed defects can be reopened if the fix doesn't hold

The **Trade / supplier** field records who is responsible — supplier notification links are coming in a later update.
        `,
            },
        ],
    },
    {
        id: 'safety.overview',
        title: 'Site Safety Registers',
        category: 'Inspections',
        order: 3,
        summary: 'Inductions, SWMS, toolbox talks, incidents and licence expiry alerts',
        route: '/safety',
        keywords: ['induction', 'SWMS', 'toolbox talk', 'incident', 'near miss', 'white card', 'licence', 'certification'],
        content: `
The Safety page (Site menu) holds four registers:

- **Inductions** — who has been inducted onto each site, with the person's signature. Works for employees and visitors.
- **SWMS** — the safe work method statement register per trade, with review dates. When a review date arrives, owners/admins are notified once. Attach the document itself via Documents and distribute with a Transmittal for acknowledgement tracking.
- **Toolbox Talks** — record the topic and collect attendee sign-on signatures on the spot.
- **Incidents** — structured injury / near-miss / hazard reporting with severity, notifiable flag, corrective actions and photos. Open incidents show as a count on the tab.

**Certifications & licences** live on each employee (Timesheets → Employees → open an employee): white cards, trade licences, insurances with expiry dates. When one is within 30 days of expiring (or expired), the employee's linked user and all owners/admins are notified once — renewing the expiry date re-arms the alert.
    `,
    },
];
